import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import API_URL from '../api';
import { postAuthDestination, safeNextPath } from '../utils/redirect';
import brandLockup from '../assets/images/company-name.jpg';
import '../ui/motion.css';
import './Login.css';

/* The right-hand panel is the only thing that changes with the role:
   what a student sees on HyrSkill, versus what a recruiter sees. */
const SCENE_COPY = {
  student: {
    title: 'Opportunities that already match you',
    body: 'Roles ranked against your skills, branch and availability — with the reason for every match spelled out.',
  },
  recruiter: {
    title: 'Verified talent, without the waiting',
    body: 'Search IIT and NIT students by skill, availability and stipend, and reach the ones who fit straight away.',
  },
};

function StudentScene() {
  return (
    <div className="lg-stage" aria-hidden="true">
      <span className="lg-chip lg-chip--accent lg-s-a">Python</span>
      <span className="lg-chip lg-s-e">Product</span>
      <span className="lg-chip lg-s-b">AI/ML</span>
      <span className="lg-chip lg-chip--accent lg-s-c">React</span>
      <span className="lg-chip lg-s-d">Data</span>

      <div className="lg-mini lg-s-card" style={{ '--lg-meter': '92%' }}>
        <div className="lg-mini__row">
          <span className="lg-mini__logo">N</span>
          <span>
            <span className="lg-mini__title">Product Engineer Intern</span>
            <span className="lg-mini__sub">Nexora · Remote</span>
          </span>
        </div>
        <div className="lg-mini__tags">
          <span className="lg-mini__tag">Internship</span>
          <span className="lg-mini__tag">₹20–25k</span>
          <span className="lg-mini__tag">6 months</span>
        </div>
        <div className="lg-mini__meter"><i /></div>
      </div>

      <span className="lg-pill lg-s-pill">92% match</span>
    </div>
  );
}

function RecruiterScene() {
  return (
    <div className="lg-stage" aria-hidden="true">
      <span className="lg-chip lg-r-a">IIT Bombay</span>
      <span className="lg-chip lg-chip--accent lg-r-b">Open to remote</span>
      <span className="lg-chip lg-r-c">4 skills matched</span>
      <span className="lg-chip lg-chip--accent lg-r-d">Available in 2 weeks</span>

      <div className="lg-mini lg-r-card" style={{ '--lg-meter': '88%' }}>
        <div className="lg-mini__row">
          <span className="lg-mini__logo">A</span>
          <span>
            <span className="lg-mini__title">3rd year · Computer Science</span>
            <span className="lg-mini__sub">IIT Jodhpur</span>
          </span>
        </div>
        <div className="lg-mini__tags">
          <span className="lg-mini__tag">Python</span>
          <span className="lg-mini__tag">React</span>
          <span className="lg-mini__tag">Node.js</span>
          <span className="lg-mini__tag">SQL</span>
        </div>
        <div className="lg-mini__meter"><i /></div>
      </div>

      <span className="lg-pill lg-r-pill">Verified profile</span>
    </div>
  );
}

function Login() {
  const [searchParams] = useSearchParams();
  const [role, setRole] = useState(searchParams.get('role') === 'recruiter' ? 'recruiter' : 'student');
  const [error, setError] = useState('');
  const [suggestRole, setSuggestRole] = useState(null); // 'student' | 'recruiter' | null
  const [loading, setLoading] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const navigate = useNavigate();

  /* Where to land once signed in. A candidate who came from a shared job link
     carries it here as ?next=/jobs/<id> and has to be put back on that job —
     dropping it is what left them on the dashboard with nothing applied for. */
  const next = safeNextPath(searchParams.get('next'));

  /* The hand-off to the dashboard is a fade, not a jump — the timer is
     cleared on unmount so a fast route change can never fire it twice. */
  const exitTimer = useRef(null);
  useEffect(() => () => { if (exitTimer.current) clearTimeout(exitTimer.current); }, []);

  const leaveTo = (to, options) => {
    setLeaving(true);
    exitTimer.current = setTimeout(() => navigate(to, options), 240);
  };

  const switchRole = (newRole) => {
    setRole(newRole);
    setError('');
    setSuggestRole(null);
  };

  const handleGoogleSuccess = async (credential) => {
    setLoading(true);
    setError('');
    setSuggestRole(null);
    try {
      const endpoint = role === 'student'
        ? `${API_URL}/api/auth/google/student`
        : `${API_URL}/api/auth/google/recruiter`;

      const { data } = await axios.post(endpoint, { id_token: credential });

      if (data.needs_profile) {
        const dest = role === 'student' ? '/student/complete-profile' : '/recruiter/complete-profile';
        // The return path rides through the profile step so a brand-new
        // candidate ends up on the job too, not just a returning one.
        leaveTo(dest, { state: { ...data, next } });
      } else {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        leaveTo(postAuthDestination(data.user?.role || role, next), { replace: true });
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Sign-in failed. Please try again.';
      const existingRole = err.response?.data?.existing_role || null;
      setError(msg);
      setSuggestRole(existingRole);
      setLoading(false);
    }
  };

  const copy = SCENE_COPY[role];

  return (
    <div className="lg-page" data-leaving={leaving ? 'true' : 'false'}>

      {/* ── Sign-in ── */}
      <div className="lg-form-side">
        <div className="lg-form-inner">
          <img src={brandLockup} alt="HyrSkill" className="lg-logo" />

          <h1 className="lg-heading">Welcome back</h1>
          <p className="lg-sub">Sign in to continue to HyrSkill.</p>

          {/* Segmented role control — the thumb slides, the scene follows. */}
          <div
            className="lg-segment"
            role="tablist"
            aria-label="Sign in as"
            style={{ '--lg-thumb-x': role === 'recruiter' ? '100%' : '0%' }}
          >
            <span className="lg-segment__thumb" aria-hidden="true" />
            {['student', 'recruiter'].map((r) => (
              <button
                key={r}
                type="button"
                role="tab"
                aria-selected={role === r}
                className={`lg-seg-btn${role === r ? ' is-active' : ''}`}
                onClick={() => switchRole(r)}
              >
                {r === 'student' ? 'Student' : 'Recruiter'}
              </button>
            ))}
          </div>

          {error && (
            <div className="alert alert-error lg-alert">
              <span>{error}</span>
              {suggestRole && (
                <button type="button" className="lg-alert__switch" onClick={() => switchRole(suggestRole)}>
                  Switch to {suggestRole === 'student' ? 'Student' : 'Recruiter'} sign-in →
                </button>
              )}
            </div>
          )}

          {loading ? (
            <div className="lg-signing">
              <span className="ui-spin" aria-hidden="true" />
              Signing you in…
            </div>
          ) : (
            <div className="lg-google">
              <GoogleLogin
                onSuccess={res => handleGoogleSuccess(res.credential)}
                onError={() => setError('Google sign-in failed. Please try again.')}
                text="continue_with"
                shape="rectangular"
                size="large"
                width="300"
                theme="outline"
              />
            </div>
          )}

          <p className="lg-note" key={role}>
            {role === 'student'
              ? 'First-time students will complete their college details after signing in.'
              : 'First-time recruiters will add their company details after signing in.'}
          </p>

          <p className="lg-foot">Sign in with your Google account. No password to remember.</p>
        </div>
      </div>

      {/* ── Ambient scene ── */}
      <div className="lg-scene-side">
        <div className="lg-scene" data-active={role === 'student' ? 'true' : 'false'}>
          <StudentScene />
        </div>
        <div className="lg-scene" data-active={role === 'recruiter' ? 'true' : 'false'}>
          <RecruiterScene />
        </div>

        <div className="lg-scene-copy">
          <h2 key={`${role}-t`}>{copy.title}</h2>
          <p key={`${role}-b`}>{copy.body}</p>
        </div>
      </div>
    </div>
  );
}

export default Login;
